import React, { useEffect, useRef, useState } from "react";
import { toast, Flip } from 'react-toastify';
import { useQuery, useDetail } from "seed/gql";
import { usePost } from "seed/api";
import * as util from "components/collections/util/tutorial";
import { CollectionModals } from "components/collections/Collections.lib.modals";
import { CollectionCallbacks, RowCallbacks, ViewCallbacks } from "components/collections/Collections.lib.callbacks";
import { ColumnActions, RowActions } from "components/collections/Collections.lib.actions";
import l from "components/util/l";
import { useUserPermissions } from "components/util/helpers/UserPermissions";
import View from "components/collections/Collections.view";

function Collections({
	flow = {},
	initialCollectionId = null,
	showTabs = true,
	isReadOnly = false,
	availableCollections = [],
	refetchParentCollections = () => { }
}) {

	const userId = sessionStorage.getItem("id");
	const isDeveloper = sessionStorage.getItem("is_developer") == "true";
	const [activeCollectionId, setActiveCollectionId] = useState(initialCollectionId);
	const [embeddingStatus, setEmbeddingStatus] = useState(null);
	const [autoSelectLastCollection, setAutoSelectLastCollection] = useState(false);
	const [isEditingTab, setIsEditingTab] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const pendingQueueRef = useRef(null);
	const tableRef = useRef(null);
	const [operationQueue, setOperationQueue] = useState([]);
	const [isFetchingData, setIsFetchingData] = useState(false);

	const [lastFilterDate, setLastFilterDate] = useState(null);
	const [sorting, setSorting] = useState([]);
	const [filters, setFilters] = useState([]);
	const [query, setQuery] = useState("");
	const [search, setSearch] = useState("");
	const [pageNum, setPageNum] = useState(1);
	const pageSize = 50;

	const [primaryKey, setPrimaryKey] = useState(null);
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);
	const [collectionOptions, setCollectionOptions] = useState({});

	const userPermissions = useUserPermissions({ userId, flowId: flow.id, collectionId: activeCollectionId });

	const collectionRoomName = `COLLECTION_STATUS_${activeCollectionId}`;

	const reqCollection = useDetail(`{
		collection {
			id
			schema
			name
			type
			settings
			uiSettings
		}
	}`, activeCollectionId);

	const reqCollections = useQuery(`{
		collections {
			id
			name
			type
			settings
			uiSettings
			schema
		}
	}`, `(flow_id=${flow.id} AND is_active=true)`, {
		onCompleted: (data) => {

			const collections = data.collections.filter(c => availableCollections.length === 0 ||
				availableCollections.map(c => c.id).includes(c.id))
					.filter(c => ((c.settings?.permissions?.show_view ?? {})[userPermissions.role?.id] ?? true) || userPermissions.is_admin)
						.filter(c => c.settings?.is_hidden != true);

			let collectionId = null;
			let changeCollection = autoSelectLastCollection || !activeCollectionId;
			if (!changeCollection) return;

			if (autoSelectLastCollection && collections.length > 0) {
				collectionId = collections[0].id;
			}
			else if (collections.length > 0 && !activeCollectionId) {

				collectionId = collections[0].id
				for (let collection of collections)
					if (collection.id == localStorage.getItem("def_collection_" + flow.id))
						collectionId = collection.id;

			}

			setActiveCollectionId(collectionId);
			setQuery(localStorage.getItem("collection_query_" + collectionId) || "");
			setFilters(JSON.parse(localStorage.getItem("collection_filters_" + collectionId) || "[]"));
			setSorting(JSON.parse(localStorage.getItem("collection_sorting_" + collectionId) || "[]"));
			setPageNum(parseInt(localStorage.getItem("collection_page_" + collectionId) || "1"));

		},
		orderBy: "-id"
	});

	const [callGetData, reqGetData] = usePost("/collections/get_data", {
		onCompleted: () => {
			setIsFetchingData(false);
		}
	});

	const [callOperation] = usePost("/collections/handle_collection_single_operation", {
		onCompleted: () => {
			setIsFetchingData(false);
		}
	});

	const refetchCollections = (autoSelectLast = false) => {
		setAutoSelectLastCollection(autoSelectLast);
		reqCollections.refetch();
	}

	const refetchActiveCollection = () =>
		reqCollection.refetch();

	const refetchData = () => 
		callGetData({
			collection_id: activeCollectionId,
			page_size: pageSize,
			page_number: pageNum,
			search,
			query,
			sorting,
			ignore_compress: true,
			filter_date: lastFilterDate
		});

	const pushOperationQueue = (operation) =>
		setOperationQueue((prevActions) => [...prevActions, operation]);

	const { collection = {} } = reqCollection.data || {};
	const { collections: auxCollections = [] } = reqCollections.data || {};
	const collectionRows = (reqGetData.data || {}).data;
	const totalPages = (reqGetData.data || {}).total_pages || 0;
	const loading = reqCollection.loading || reqCollections.loading || reqGetData.loading || isLoading;

	const {
		collectionCreateModal,
    collectionEditModal,
    collectionImportModal,
    columnCreateModal,
    columnEditModal,
    settingsModal,
    permissionModal,
    onClickOpenCollectionCreateModal,
    onClickCloseCollectionCreateModal,
    onClickOpenCollectionEditModal,
    onClickCloseCollectionEditModal,
    onClickOpenCollectionImportModal,
    onClickCloseCollectionImportModal,
    onClickOpenColumnCreateModal,
    onClickCloseColumnCreateModal,
    onClickOpenColumnEditModal,
    onClickCloseColumnEditModal,
    onClickOpenSettingsModal,
    onClickCloseSettingsModal,
    onClickOpenPermissionModal,
    onClickClosePermissionModal
	} = CollectionModals();

	const { 
		onSaveCollection, 
		onEditCollection, 
		onChangeCollectionName,
		onClickCreateCollection,
		onClickCloneCollection, 
		onClickChangeTab, 
		onClickDeleteCollection 
	} = CollectionCallbacks({
		tableRef,
		flowId: flow.id, 
		activeCollectionId, 
		setActiveCollectionId, 
		auxCollections,
		setSearch, 
		setQuery, 
		setFilters, 
		setSorting, 
		setPageNum,
		setLastFilterDate,
		setEmbeddingStatus, 
		refetchCollections, 
		refetchParentCollections, 
		refetchActiveCollection, 
		refetchData,
	});

	const {
		onClickDeleteRow,
    onClickDeleteMultipleRows,
		onClickClearData,
	} = RowCallbacks({
		tableRef,
		activeCollectionId,
		rows,
		pushOperationQueue,
		setIsLoading,
	});

	const {
		onClickImport,
    onClickExport,
    onClickDownloadZip,
    onClickCell
	} = ViewCallbacks({
		activeCollectionId,
		collectionOptions,
		rows,
		columns,
		totalPages,
		setRows,
		setSearch,
		setFilters,
		setQuery,
		setSorting,
		setPageNum,
		setIsLoading,
		refetchData,
		refetchActiveCollection,
	});

	const { 
		setCollectionColumns,
		setForeignCollectionOptions,
		updateSearch,
		updateFilters,
		updateQuery,
		updateSorting,
		updatePage,
    pushColumnOperation
	} = ColumnActions({ 
		activeCollectionId,
		pushOperationQueue,
		totalPages,
		setSearch,
		setFilters,
		setQuery,
		setSorting,
		setPageNum,
		setLastFilterDate,
		setColumns,
		setPrimaryKey,
		setCollectionOptions,
	});

	const { 
		setCollectionRows,
		pushRowOperation 
	} = RowActions({ 
		activeCollectionId, 
		pushOperationQueue,
		setRows,
	});

	const onCollectionMessage = ({ data }) => {

		setIsLoading(false);

		const { status, message = {} } = data;

		if(status == "UPDATE_DATA") {

			if(message.should_update) {
				refetchData();
			}

			if(message.error == "collection_limit_exceeded" && !toast.isActive("error-toast")) {
				toast(l("El tamaño del archivo excede el límite de tu licencia%CA137"), {
					position: "top-right",
					autoClose: 2000,
					hideProgressBar: true,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					theme: "light",
					toastId: "error-toast",
					transition: Flip,
					type: "error"
				});	
			}

		}

		if (status == "UPDATE_SCHEMA") {
			refetchActiveCollection();
		}

		if (status == "UPDATE_EMBEDDINGS") {
			
			const { current, total } = message;
			setEmbeddingStatus(current / total * 100);

			if(current == total)
				setTimeout(() => setEmbeddingStatus(null), 1000);

		}

	}

	const isAdmin = userPermissions?.is_admin;
	const showPermissions = isAdmin;
	const showManageCollections = isAdmin || (userPermissions?.flow?.show_manage_collections ?? false);
	const showView = isAdmin || (userPermissions?.collection?.show_view ?? false);
	const showAdd = isAdmin || (userPermissions?.collection?.show_add ?? false);
	const showDelete = isAdmin || (userPermissions?.collection?.show_delete ?? false);

	let collections = auxCollections
		.filter(c => availableCollections.length === 0 || availableCollections.map(c => c.id).includes(c.id))
			.filter(c => ((c.settings?.permissions?.show_view ?? {})[userPermissions?.role?.id] ?? true) || userPermissions?.is_admin)
				.filter(c => c.settings?.is_hidden != true);

	collections = collections.sort((a, b) => {
		if (a.settings?.index != null && b.settings?.index != null)
			return a.settings.index - b.settings.index;
		else
			return b.id - a.id;
	});

	useEffect(() => {
		
		if(!availableCollections || availableCollections.length === 0) 
			return;
		
		setActiveCollectionId(null);
		refetchCollections();

	}, [availableCollections]);

	useEffect(() => {
		refetchCollections();
	}, [flow]);

	useEffect(() => {
		setRows([]);
		refetchActiveCollection();
		refetchData();
	}, [pageNum, activeCollectionId, search, query, sorting]);

	useEffect(() => {
		refetchCollections();
	}, [activeCollectionId]);

	useEffect(() => {
		setCollectionColumns(collection);
	}, [collection]);

	useEffect(() => {
		setCollectionRows(collectionRows, sorting);
	}, [collectionRows, sorting]);

	useEffect(() => {

		if(operationQueue.length == 0) return;
		if(isFetchingData) return;

		const actionTypes = operationQueue.reduce((acc, action) => {
			if(action.type == "data")
				action.actions.forEach(a => {
					acc[a.action] = (acc[a.action] || 0) + 1;
				});
			return acc;
		}, {});

		if(operationQueue.length == actionTypes["update"]) {

			const randomKey = Math.random().toString(36).substring(2, 15);
			pendingQueueRef.current = randomKey;

			setTimeout(() => {

				if(randomKey != pendingQueueRef.current) return;

				if(operationQueue.length == actionTypes["update"]) {

					const mergedOperations = operationQueue[0];

					for(let i = 1; i < operationQueue.length; i++) {
						const operation = operationQueue[i];
						mergedOperations.actions.push(...operation.actions);
					}

					callOperation(mergedOperations);
					setOperationQueue([]);
					setIsFetchingData(true);

				}

			}, 2000);

		}
		else {

			const operation = operationQueue[0];
			callOperation(operation);
			setOperationQueue(operationQueue.slice(1));
			setIsFetchingData(true);

		}

	}, [operationQueue, isFetchingData]);

	useEffect(() => {
		setForeignCollectionOptions(columns);
  }, [columns]);

	return <View
		tableRef={tableRef}
		flow={flow}
		activeCollectionId={activeCollectionId}
		collectionRoomName={collectionRoomName}
		height={totalPages > 1 ? "calc(100% - 180px)" : "calc(100% - 127px)"}
		tutorialSteps={util.dataTutorialSteps}
		embeddingStatus={embeddingStatus}
		collections={collections}
		collectionOptions={collectionOptions}
		primaryKey={primaryKey}
		columns={columns}
		rows={rows}
		pageNum={pageNum}
		pageSize={pageSize}
		totalPages={totalPages}
		query={query}
		filters={filters}
		search={search}
		sorting={sorting}
		isDeveloper={isDeveloper}
		isReadOnly={isReadOnly}
		isEditingTab={isEditingTab}
		isLoading={loading}
		showTabs={showTabs}
		showPermissions={showPermissions}
		showManageCollections={showManageCollections}
		showAdd={showAdd}
		showView={showView}
		showDelete={showDelete}
		collectionCreateModal={collectionCreateModal}
		collectionEditModal={collectionEditModal}
		collectionImportModal={collectionImportModal}
		columnCreateModal={columnCreateModal}
		columnEditModal={columnEditModal}
		settingsModal={settingsModal}
		permissionModal={permissionModal}
		setColumns={setColumns}
		setActiveCollectionId={setActiveCollectionId}
		setIsEditingTab={setIsEditingTab}
		onSaveCollection={onSaveCollection}
		onEditCollection={onEditCollection}
		onChangeCollectionName={onChangeCollectionName}
		onClickCreateCollection={onClickCreateCollection}
		onClickCloneCollection={onClickCloneCollection}
		onClickChangeTab={onClickChangeTab}
		onClickDeleteCollection={onClickDeleteCollection}
		onClickDeleteRow={onClickDeleteRow}
    onClickDeleteMultipleRows={onClickDeleteMultipleRows}
		onClickClearData={onClickClearData}
    onClickImport={onClickImport}
    onClickExport={onClickExport}
    onClickDownloadZip={onClickDownloadZip}
    onClickCell={onClickCell}
		onCollectionMessage={onCollectionMessage}
		onClickOpenCollectionCreateModal={onClickOpenCollectionCreateModal}
		onClickCloseCollectionCreateModal={onClickCloseCollectionCreateModal}
		onClickOpenCollectionEditModal={onClickOpenCollectionEditModal}
		onClickCloseCollectionEditModal={onClickCloseCollectionEditModal}
		onClickOpenCollectionImportModal={onClickOpenCollectionImportModal}
		onClickCloseCollectionImportModal={onClickCloseCollectionImportModal}
		onClickOpenColumnCreateModal={onClickOpenColumnCreateModal}
		onClickCloseColumnCreateModal={onClickCloseColumnCreateModal}
		onClickOpenColumnEditModal={onClickOpenColumnEditModal}
		onClickCloseColumnEditModal={onClickCloseColumnEditModal}
		onClickOpenSettingsModal={onClickOpenSettingsModal}
		onClickCloseSettingsModal={onClickCloseSettingsModal}
		onClickOpenPermissionModal={onClickOpenPermissionModal}
		onClickClosePermissionModal={onClickClosePermissionModal}
		pushColumnOperation={pushColumnOperation}
		pushRowOperation={pushRowOperation}
		updateSearch={updateSearch}
		updateFilters={updateFilters}
		updateQuery={updateQuery}
		updateSorting={updateSorting}
		updatePage={updatePage}
		refetchCollections={refetchCollections}
	/>;

}

export default Collections;